import { useState } from "react";
import { api } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ArrowRight, ArrowLeft, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Company","Problem","Communication","Resolution"];

export default function CFPB() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ company:"", problem_type:"Inaccurate information on report", problem_start:"", inaccurate_info:"", steps_taken:"", company_response:"", resolution_requested:"", supporting_docs:"" });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k,v)=>setF(s=>({...s, [k]: v}));

  const next = () => setStep(s=>Math.min(3, s+1));
  const back = () => setStep(s=>Math.max(0, s-1));

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/cfpb/draft", f);
      setDraft(r.data);
      toast.success("CFPB draft generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    if (!draft) return;
    const txt = `SUMMARY\n${draft.summary}\n\nNARRATIVE\n${draft.narrative}\n\nTIMELINE\n${(draft.timeline||[]).map(t=>`- ${t.date}: ${t.event}`).join("\n")}\n\nRESOLUTION REQUESTED\n${draft.resolution}\n\nSUPPORTING DOCUMENTS\n${(draft.supporting_docs||[]).map(d=>`- ${d}`).join("\n")}`;
    await navigator.clipboard.writeText(txt);
    toast.success("Copied CFPB complaint");
  };

  return (
    <AppShell>
      <div>
        <div className="eyebrow">CFPB Complaint Builder</div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-1">Draft your CFPB complaint</h1>
        <p className="text-slate-500 text-sm mt-1">A guided wizard produces a narrative, timeline, and requested resolution. Nothing is submitted automatically.</p>
      </div>

      <div className="mt-6 grid grid-cols-4 gap-2">
        {STEPS.map((s,i)=>(
          <div key={s} className={`rounded-xl p-3 border ${i===step?"border-purple-400/60 bg-purple-500/10":"border-slate-200 bg-slate-50/70"}`}>
            <div className="eyebrow">Step {i+1}</div>
            <div className={`text-sm font-medium ${i===step?"text-slate-900":"text-slate-700"}`}>{s}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <Card className="glass rounded-2xl">
          <CardContent className="p-6 space-y-4">
            {step === 0 && (
              <>
                <div>
                  <Label className="text-slate-700">Which company is involved?</Label>
                  <Input value={f.company} onChange={(e)=>set("company", e.target.value)} data-testid="cfpb-company-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" placeholder="e.g., Equifax, Midland Credit"/>
                </div>
                <div>
                  <Label className="text-slate-700">Type of credit-reporting problem</Label>
                  <Input value={f.problem_type} onChange={(e)=>set("problem_type", e.target.value)} data-testid="cfpb-problem-type-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                </div>
              </>
            )}
            {step === 1 && (
              <>
                <div>
                  <Label className="text-slate-700">When did the problem begin?</Label>
                  <Input value={f.problem_start} onChange={(e)=>set("problem_start", e.target.value)} data-testid="cfpb-problem-start-input" placeholder="e.g., 2024-05" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                </div>
                <div>
                  <Label className="text-slate-700">What information is inaccurate?</Label>
                  <Textarea value={f.inaccurate_info} onChange={(e)=>set("inaccurate_info", e.target.value)} data-testid="cfpb-inaccurate-info" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                </div>
              </>
            )}
            {step === 2 && (
              <>
                <div>
                  <Label className="text-slate-700">What steps have you already taken?</Label>
                  <Textarea value={f.steps_taken} onChange={(e)=>set("steps_taken", e.target.value)} data-testid="cfpb-steps-taken" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                </div>
                <div>
                  <Label className="text-slate-700">How did the company respond?</Label>
                  <Textarea value={f.company_response} onChange={(e)=>set("company_response", e.target.value)} data-testid="cfpb-company-response" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                </div>
              </>
            )}
            {step === 3 && (
              <>
                <div>
                  <Label className="text-slate-700">What resolution are you requesting?</Label>
                  <Textarea value={f.resolution_requested} onChange={(e)=>set("resolution_requested", e.target.value)} data-testid="cfpb-resolution" className="mt-1 bg-slate-50 border-slate-200 text-slate-900"/>
                </div>
                <div>
                  <Label className="text-slate-700">Supporting documents (comma-separated)</Label>
                  <Input value={f.supporting_docs} onChange={(e)=>set("supporting_docs", e.target.value)} data-testid="cfpb-supporting-docs" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" placeholder="e.g., ID, dispute letter copy, bureau response"/>
                </div>
              </>
            )}
            <div className="flex justify-between pt-2">
              <Button variant="outline" disabled={step===0} onClick={back} data-testid="cfpb-back-button" className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800"><ArrowLeft size={16} className="mr-1"/>Back</Button>
              {step < 3 ? (
                <Button onClick={next} data-testid="cfpb-next-button" className="btn-primary rounded-xl">Next<ArrowRight size={16} className="ml-1"/></Button>
              ) : (
                <Button onClick={generate} disabled={busy || !f.company} data-testid="cfpb-generate-button" className="btn-primary rounded-xl">
                  {busy ? <><Loader2 className="animate-spin mr-2" size={16}/>Drafting…</> : "Generate Draft"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="glass rounded-2xl">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-slate-900 font-semibold">Draft preview</div>
              {draft && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={copy} className="btn-primary rounded-xl" data-testid="cfpb-copy-button"><Copy size={14} className="mr-1"/>Copy</Button>
                  <a href="https://www.consumerfinance.gov/complaint/" target="_blank" rel="noopener noreferrer" data-testid="cfpb-external-link">
                    <Button size="sm" variant="outline" className="rounded-xl border-slate-200 bg-slate-50/70 text-slate-800"><ExternalLink size={14} className="mr-1"/>CFPB site</Button>
                  </a>
                </div>
              )}
            </div>
            {!draft && <div className="text-slate-500 text-sm">Complete the wizard to preview your complaint.</div>}
            {draft && (
              <div className="space-y-4 text-sm">
                <div>
                  <div className="eyebrow">Summary</div>
                  <p className="text-slate-800 mt-1" data-testid="cfpb-summary">{draft.summary}</p>
                </div>
                <div>
                  <div className="eyebrow">Narrative</div>
                  <p className="whitespace-pre-wrap text-slate-800 mt-1" data-testid="cfpb-narrative">{draft.narrative}</p>
                </div>
                <div>
                  <div className="eyebrow">Timeline</div>
                  <ul className="mt-1 list-disc list-inside text-slate-800 space-y-1" data-testid="cfpb-timeline">
                    {(draft.timeline||[]).map((t,i)=><li key={i}><span className="text-purple-700">{t.date}</span> — {t.event}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="eyebrow">Resolution requested</div>
                  <p className="text-slate-800 mt-1" data-testid="cfpb-resolution-preview">{draft.resolution}</p>
                </div>
                <div>
                  <div className="eyebrow">Supporting documents</div>
                  <ul className="mt-1 list-disc list-inside text-slate-800 space-y-1">
                    {(draft.supporting_docs||[]).map((d,i)=><li key={i}>{d}</li>)}
                  </ul>
                </div>
                <p className="text-xs text-slate-400">The MVP does not submit the complaint automatically. Copy your narrative and file it on the official CFPB site.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
